// ============================================================
// ProvisionTrialDialog.js
// Single-screen trial approval: the operator sees Tenant, Client AND
// Invitation fields (all editable) and clicks ONE "Approve & provision"
// button. Behind that button the orchestrator runs the sequential
// calls — createTenant → createClient → provision → (sample data,
// on by default, non-fatal; 2026-10-20) → issueInvitation —
// with inline step progress (spinner → green tick per step; red cross
// + reason on failure).
//
// Architecture note (load-bearing — see CLAUDE.md gotcha
// "Trial approval flow is frontend-orchestrated, not
// backend-transactional"): this is deliberately NOT a backend
// transaction. Each step is its own call. Created Tenant/Client IDs
// and the provisioned trial are held in state so a Retry resumes from
// the failed step rather than re-creating.
//
// Invitation failure does NOT roll back Tenant/Client/provision. If
// issueInvitation fails (any reason), the trial stays Provisioned and
// the Client is flagged via markClientManualInviteRequired so the
// operator can re-send from the client detail page. The dialog surfaces
// "Trial provisioned but invitation send failed — see client record".
//
// Invite role defaults to Manager (the RM/NI evaluating the platform),
// editable. Only manager/carer are offered here — admin/auditor need
// extra fields (agent entitlements / access duration) the issue handler
// requires and this dialog does not collect; use the Invitations page
// for those.
// ============================================================

import React, { useEffect, useState } from 'react';
import { Loader2, Check, X } from 'lucide-react';
import Modal from '../components/Modal';
import Field from '../components/Field';
import { createTenant } from '../api/tenants';
import { createAdminClient, markClientManualInviteRequired } from '../api/adminClients';
import { provisionAdminTrialRequest } from '../api/adminTrialRequests';
import { addClientSampleData } from '../api/adminClientConfig';
import { issueInvitation } from '../api/invitations';
import { translateError } from '../utils/errorMessages';
import { toast } from '../components/Toaster';
import StatusMessage from '../components/StatusMessage';
import { IDENTITY_PROVIDER_OPTIONS } from './CreateTenantDialog';
import './CreateTenantDialog.css';
import './ProvisionTrialDialog.css';

const CLIENT_TYPE_LABELS = {
  care_home:          'Care home',
  domiciliary_agency: 'Domiciliary agency',
  mixed:              'Mixed',
};

const INVITE_ROLE_LABELS = {
  manager: 'Manager (full platform — recommended for the trial)',
  carer:   'Carer (CareNotes only)',
};

const STEP_DEFS = [
  { key: 'tenant',    label: 'Creating tenant' },
  { key: 'client',    label: 'Creating client' },
  { key: 'provision', label: 'Linking trial' },
  { key: 'sample',    label: 'Adding sample data' },
  { key: 'invite',    label: 'Sending invitation' },
];

const INITIAL_STEP_STATUS = { tenant: 'pending', client: 'pending', provision: 'pending', sample: 'pending', invite: 'pending' };

function isValidEmail(value) {
  return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

// A 1-500 char human-readable reason captured for ManualInviteReason.
// ApiError never carries the invitation token; message/detail are the
// server's error body fields, safe to store/show.
function buildInviteFailureReason(err) {
  if (err && err.isNetworkError) return 'Network error contacting the invitation API.';
  const status = err && err.status != null ? err.status : '?';
  const msg = (err && (err.message || err.detail)) || 'unknown error';
  let reason = `HTTP ${status}: ${msg}`;
  if (err && err.detail && err.detail !== err.message) reason += ` — ${err.detail}`;
  return reason.slice(0, 500);
}

export default function ProvisionTrialDialog({ isOpen, onClose, trial, onProvisioned }) {
  // Created-resource state (held so Retry resumes, never re-creates).
  const [createdTenant, setCreatedTenant] = useState(null);
  const [createdClient, setCreatedClient] = useState(null);
  const [provisionedTrial, setProvisionedTrial] = useState(null);
  // Sample data (2026-10-20, on by default): added before the invitation so
  // the account is not empty at the first sign-in. Never blocks approval.
  const [addSample, setAddSample] = useState(true);
  const [sampleDone, setSampleDone] = useState(false);

  // Form state.
  // identityProvider defaults to 'other' (2026-09-03): it is checked by
  // hand before provisioning, so Microsoft / Google are chosen, never
  // assumed. Editable later on the tenant page.
  const [tenantForm, setTenantForm] = useState({ tenantName: '', identityProvider: 'other', externalTenantId: '' });
  const [clientForm, setClientForm] = useState({ clientName: '', clientType: 'care_home' });
  const [inviteForm, setInviteForm] = useState({ displayName: '', email: '', roleKey: 'manager' });

  // Run state.
  const [started, setStarted] = useState(false);
  const [running, setRunning] = useState(false);
  const [stepStatus, setStepStatus] = useState(INITIAL_STEP_STATUS);
  const [stepError, setStepError] = useState({});
  const [inviteOutcome, setInviteOutcome] = useState(null); // 'sent' | 'failed'
  const [finalBanner, setFinalBanner] = useState(null);      // { tone, text }
  const [errors, setErrors] = useState({});

  // Reset whenever (re)opened for a given trial.
  useEffect(() => {
    if (!isOpen || !trial) return;
    setCreatedTenant(null);
    setCreatedClient(null);
    setProvisionedTrial(null);
    setAddSample(true);
    setSampleDone(false);
    setTenantForm({ tenantName: trial.organisationName || '', identityProvider: 'other', externalTenantId: '' });
    setClientForm({ clientName: trial.organisationName || '', clientType: 'care_home' });
    setInviteForm({
      displayName: `${trial.firstName || ''} ${trial.lastName || ''}`.trim(),
      email: trial.email || '',
      roleKey: 'manager',
    });
    setStarted(false);
    setRunning(false);
    setStepStatus(INITIAL_STEP_STATUS);
    setStepError({});
    setInviteOutcome(null);
    setFinalBanner(null);
    setErrors({});
  }, [isOpen, trial]);

  function handleClose() {
    if (running) return;
    onClose();
  }

  function setStep(key, status, reason) {
    setStepStatus((prev) => ({ ...prev, [key]: status }));
    setStepError((prev) => ({ ...prev, [key]: status === 'error' ? reason : null }));
  }

  function validateForm() {
    const e = {};
    if (!tenantForm.tenantName.trim()) e.tenantName = 'Tenant name is required.';
    if (!clientForm.clientName.trim()) e.clientName = 'Client name is required.';
    if (!inviteForm.displayName.trim()) e.displayName = 'Recipient name is required.';
    if (!isValidEmail(inviteForm.email)) e.email = 'A valid recipient email is required.';
    return e;
  }

  function handleApprove(event) {
    event.preventDefault();
    const e = validateForm();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    runApprove();
  }

  // Sequential orchestrator. Hard steps (tenant/client/provision) stop
  // on failure (Retry resumes). Invite failure is non-fatal — the trial
  // stays provisioned and the client is flagged for manual invite.
  async function runApprove() {
    setStarted(true);
    setRunning(true);
    setFinalBanner(null);

    let tenant = createdTenant;
    let client = createdClient;
    let trialRow = provisionedTrial;

    // ── Step 1: tenant ──
    if (!tenant) {
      setStep('tenant', 'running');
      try {
        const body = { tenantName: tenantForm.tenantName.trim(), identityProvider: tenantForm.identityProvider };
        if (tenantForm.externalTenantId.trim()) body.externalTenantId = tenantForm.externalTenantId.trim();
        tenant = await createTenant(body);
        setCreatedTenant(tenant);
        setStep('tenant', 'done');
      } catch (err) {
        setStep('tenant', 'error', translateError(err));
        setRunning(false);
        return;
      }
    } else {
      setStep('tenant', 'done');
    }

    // ── Step 2: client ──
    if (!client) {
      setStep('client', 'running');
      try {
        client = await createAdminClient({
          clientName: clientForm.clientName.trim(),
          clientType: clientForm.clientType,
          tenantId: tenant.tenantId,
        });
        setCreatedClient(client);
        setStep('client', 'done');
      } catch (err) {
        setStep('client', 'error', translateError(err));
        setRunning(false);
        return;
      }
    } else {
      setStep('client', 'done');
    }

    // ── Step 3: provision (link + start clock) ──
    if (!trialRow) {
      setStep('provision', 'running');
      try {
        trialRow = await provisionAdminTrialRequest(trial.trialRequestId, {
          tenantId: tenant.tenantId,
          clientId: client.clientId,
        });
        setProvisionedTrial(trialRow);
        setStep('provision', 'done');
      } catch (err) {
        setStep('provision', 'error', translateError(err));
        setRunning(false);
        return;
      }
    } else {
      setStep('provision', 'done');
    }

    // ── Step 4: sample data (NON-FATAL, skipped when unticked) ──
    let sampleFailed = false;
    if (!addSample) {
      setStep('sample', 'skipped');
    } else if (sampleDone) {
      setStep('sample', 'done');
    } else {
      setStep('sample', 'running');
      try {
        await addClientSampleData(client.clientId);
        setSampleDone(true);
        setStep('sample', 'done');
      } catch (err) {
        sampleFailed = true;
        setStep('sample', 'error', translateError(err));
      }
    }

    // ── Step 5: invitation (NON-FATAL) ──
    setStep('invite', 'running');
    try {
      await issueInvitation({
        clientId: client.clientId,
        email: inviteForm.email.trim(),
        displayName: inviteForm.displayName.trim(),
        roleKey: inviteForm.roleKey,
      });
      setStep('invite', 'done');
      setInviteOutcome('sent');
      onProvisioned(trialRow);
      if (sampleFailed) {
        // Keep the dialog open so the operator sees the sample step failed.
        setFinalBanner({
          tone: 'warning',
          text: 'Trial provisioned and invitation sent, but the sample data could not be added. Add it from the Sample Data card on the Platform Config tab of the client.',
        });
        return;
      }
      toast.success('Trial provisioned and invitation sent');
      onClose();
      return;
    } catch (err) {
      const reason = buildInviteFailureReason(err);
      setStep('invite', 'error', reason);
      setInviteOutcome('failed');
      // Flag the client so the failure is visible + re-sendable. Best
      // effort — never throws into this flow (the trial IS provisioned).
      let markFailed = false;
      try {
        await markClientManualInviteRequired(client.clientId, reason);
      } catch (markErr) {
        markFailed = true;
      }
      // Always surface the recipient details on the banner so the
      // operator can act WITHOUT relying on the client-record flag —
      // critical in the double-failure case (mark also failed), where
      // the flag won't be there to re-send from.
      setFinalBanner({
        tone: 'warning',
        text: markFailed
          ? 'Trial provisioned, but the invitation could not be sent AND recording the flag on the client record also failed. Send the invitation manually to the recipient below, then set the flag on the client record yourself.'
          : 'Trial provisioned, but the invitation could not be sent — re-send it from the client record (details below).',
        recipient: {
          displayName: inviteForm.displayName.trim(),
          email: inviteForm.email.trim(),
          roleKey: inviteForm.roleKey,
        },
      });
      onProvisioned(trialRow); // trial is Provisioned; refresh the parent
    } finally {
      setRunning(false);
    }
  }

  const hardStepErrored = ['tenant', 'client', 'provision'].some((k) => stepStatus[k] === 'error');

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Approve & Provision Trial" closeDisabled={running}>
      <form className="create-tenant-form provision-trial" onSubmit={handleApprove} noValidate>
        <p className="provision-trial__hint">
          Approving creates the Tenant + Client for <strong>{trial?.organisationName}</strong>,
          starts the 30-day trial, and emails an invitation to the requester. Edit any field
          before approving.
        </p>

        {finalBanner && (
          <div
            className={`provision-trial__final provision-trial__final--${finalBanner.tone}`}
            role="alert"
          >
            <p className="provision-trial__final-text">{finalBanner.text}</p>
            {finalBanner.recipient && (
              <dl className="provision-trial__recipient">
                <div><dt>Name</dt><dd>{finalBanner.recipient.displayName || '—'}</dd></div>
                <div><dt>Email</dt><dd>{finalBanner.recipient.email || '—'}</dd></div>
                <div><dt>Role</dt><dd>{finalBanner.recipient.roleKey}</dd></div>
              </dl>
            )}
          </div>
        )}

        {/* ── Tenant ── */}
        <fieldset className="provision-trial__section" disabled={started}>
          <legend className="provision-trial__legend">Tenant</legend>
          <Field label="Tenant name" error={errors.tenantName} required>
            <input
              type="text"
              value={tenantForm.tenantName}
              maxLength={200}
              onChange={(e) => setTenantForm({ ...tenantForm, tenantName: e.target.value })}
              autoFocus
            />
          </Field>
          <Field label="Identity provider" required>
            <select
              value={tenantForm.identityProvider}
              onChange={(e) => setTenantForm({ ...tenantForm, identityProvider: e.target.value })}
            >
              {IDENTITY_PROVIDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Client tenant directory (tenant) ID"
            help="Optional. The client's M365 / Workspace directory (tenant) ID, if known."
          >
            <input
              type="text"
              value={tenantForm.externalTenantId}
              maxLength={100}
              onChange={(e) => setTenantForm({ ...tenantForm, externalTenantId: e.target.value })}
            />
          </Field>
        </fieldset>

        {/* ── Client ── */}
        <fieldset className="provision-trial__section" disabled={started}>
          <legend className="provision-trial__legend">Client</legend>
          <Field label="Client name" error={errors.clientName} required>
            <input
              type="text"
              value={clientForm.clientName}
              maxLength={200}
              onChange={(e) => setClientForm({ ...clientForm, clientName: e.target.value })}
            />
          </Field>
          <Field label="Client type" required>
            <select
              value={clientForm.clientType}
              onChange={(e) => setClientForm({ ...clientForm, clientType: e.target.value })}
            >
              {Object.entries(CLIENT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </Field>
        </fieldset>

        {/* ── Invitation ── */}
        <fieldset className="provision-trial__section" disabled={started}>
          <legend className="provision-trial__legend">Invitation</legend>
          <Field label="Recipient name" error={errors.displayName} required>
            <input
              type="text"
              value={inviteForm.displayName}
              maxLength={200}
              onChange={(e) => setInviteForm({ ...inviteForm, displayName: e.target.value })}
            />
          </Field>
          <Field label="Recipient email" error={errors.email} required>
            <input
              type="email"
              value={inviteForm.email}
              maxLength={200}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            />
          </Field>
          <Field label="Role" required>
            <select
              value={inviteForm.roleKey}
              onChange={(e) => setInviteForm({ ...inviteForm, roleKey: e.target.value })}
            >
              {Object.entries(INVITE_ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </Field>
        </fieldset>

        {/* ── Sample data ── */}
        <fieldset className="provision-trial__section" disabled={started}>
          <legend className="provision-trial__legend">Sample data</legend>
          <label className="provision-trial__checkbox">
            <input type="checkbox" checked={addSample} onChange={(e) => setAddSample(e.target.checked)} />{' '}
            Add sample data so the account is not empty (people receiving care, staff, CareTrack events,
            policies and training). The customer can remove it at any time.
          </label>
        </fieldset>

        {/* ── Step progress (after Approve) ── */}
        {started && (
          <ol className="provision-steps">
            {STEP_DEFS.map((s) => (
              <li key={s.key} className={`provision-steps__item is-${stepStatus[s.key]}`}>
                <span className="provision-steps__icon">
                  {stepStatus[s.key] === 'done' && <Check size={14} />}
                  {stepStatus[s.key] === 'running' && <Loader2 size={14} className="create-tenant-form__spinner" />}
                  {stepStatus[s.key] === 'error' && <X size={14} />}
                  {(stepStatus[s.key] === 'pending' || stepStatus[s.key] === 'skipped') && <span className="provision-steps__dot" />}
                </span>
                <span className="provision-steps__label">{s.label}{stepStatus[s.key] === 'skipped' ? ' (skipped)' : ''}</span>
                {stepError[s.key] && <StatusMessage as="span" variant="error" className="provision-steps__error">{stepError[s.key]}</StatusMessage>}
              </li>
            ))}
          </ol>
        )}

        {/* ── Actions ── */}
        <div className="create-tenant-form__actions">
          {!started && (
            <>
              <button type="button" className="create-tenant-form__cancel" onClick={handleClose}>
                Cancel
              </button>
              <button type="submit" className="create-tenant-form__submit">
                Approve &amp; provision
              </button>
            </>
          )}

          {started && hardStepErrored && (
            <>
              <button type="button" className="create-tenant-form__cancel" onClick={handleClose} disabled={running}>
                Close
              </button>
              <button type="button" className="create-tenant-form__submit" onClick={runApprove} disabled={running}>
                {running && <Loader2 size={14} className="create-tenant-form__spinner" />}
                Retry
              </button>
            </>
          )}

          {started && !hardStepErrored && (inviteOutcome === 'failed' || (inviteOutcome === 'sent' && finalBanner)) && (
            <button type="button" className="create-tenant-form__submit" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
